/**
 * Funcionalidades:
 * - Muestra información de la sala
 * - Lista las listas de la sala
 * - Permite crear listas dentro de la sala
 * - Permite invitar usuarios
 * - Editar sala
 * - Eliminar sala
 */

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";


import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import GroupsIcon from "@mui/icons-material/Groups";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import api from "../api/axios";
import { editarSala, eliminarSala } from "../api/salas";

import CrearListaEnSalaModal from "../components/CrearListaModal";
import InvitarUsuarioModal from "../components/InvitarUsuarioModal";

export default function SalaDetalle() {
    const { idSala } = useParams();
    const navigate = useNavigate();

    // Datos
    const [sala, setSala] = useState(null);
    const [listas, setListas] = useState([]);

    // Estados UI
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState(false);

    // Modales existentes
    const [openCrearLista, setOpenCrearLista] = useState(false);
    const [openInvitar, setOpenInvitar] = useState(false);

    // Editar sala
    const [openEditar, setOpenEditar] = useState(false);
    const [nombre, setNombre] = useState("");
    const [descripcion, setDescripcion] = useState("");
    const [guardando, setGuardando] = useState(false);

    //Eliminar sala
    const [openEliminar, setOpenEliminar] = useState(false);

    async function cargarSala() {
        setCargando(true);
        setError(false);

        try {
            const { data } = await api.get(`/salas/${idSala}`);
            setSala(data);
            setListas(Array.isArray(data.listas) ? data.listas : []);
            setNombre(data.nombre);
            setDescripcion(data.descripcion || "");
        } catch {
            setError(true);
        } finally {
            setCargando(false);
        }
    }

    useEffect(() => {
        cargarSala();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idSala]);

    async function handleGuardarSala() {
        if (!nombre.trim()) return;

        setGuardando(true);
        try {
            await editarSala(idSala, {
                nombre: nombre.trim(),
                descripcion: descripcion || null,
            });
            setOpenEditar(false);
            await cargarSala();
        } catch (err) {
            alert(
                err.response?.status === 403
                    ? "No tienes permisos para editar esta sala."
                    : "No se pudo editar la sala."
            );
        } finally {
            setGuardando(false);
        }
    }

    async function handleEliminarSala() {
        try {
            await eliminarSala(idSala);
            navigate("/salas");
        } catch (err) {
            alert(
                err.response?.status === 403
                    ? "No tienes permisos para eliminar esta sala."
                    : "No se pudo eliminar la sala."
            );
        }
    }

    // Loading
    if (cargando) {
        return (
            <Stack direction="row" spacing={1.5} alignItems="center">
                <CircularProgress size={22} />
                <Typography variant="body2">Cargando sala…</Typography>
            </Stack>
        );
    }

    // Error
    if (error || !sala) {
        return (
            <Card>
                <CardContent>
                    <Typography sx={{ fontWeight: 900 }}>
                        Error
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        No se pudo cargar la sala.
                    </Typography>

                    <Button
                        sx={{ mt: 2 }}
                        variant="outlined"
                        onClick={() => navigate("/salas")}
                    >
                        Volver a salas
                    </Button>
                </CardContent>
            </Card>
        );
    }

    return (
        <Box>
            {/* Header */}
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 2 }}
            >
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate("/salas")}
                >
                    Salas
                </Button>

                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 950 }}>
                        {sala.nombre}
                    </Typography>
                    {sala.descripcion && (
                        <Typography variant="body2" sx={{ opacity: 0.75 }}>
                            {sala.descripcion}
                        </Typography>
                    )}
                </Box>

                {/* Acciones de sala */}
                <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => setOpenEditar(true)}
                >
                    Editar
                </Button>

                <Button
                    color="error"
                    variant="outlined"
                    startIcon={<DeleteIcon />}
                    onClick={() => setOpenEliminar(true)}
                >
                    Eliminar
                </Button>

                <Button
                    variant="outlined"
                    startIcon={<GroupsIcon />}
                    onClick={() => setOpenInvitar(true)}
                >
                    Invitar
                </Button>

                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenCrearLista(true)}
                >
                    Nueva lista
                </Button>
            </Stack>

            {/* Listas */}
            {listas.length === 0 ? (
                <Card>
                    <CardContent>
                        <Typography sx={{ fontWeight: 900 }}>
                            Esta sala aún no tiene listas
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
                            Crea una lista para empezar.
                        </Typography>

                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setOpenCrearLista(true)}
                        >
                            Crear lista
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <Stack spacing={1.25}>
                    {listas.map((l) => (
                        <Card
                            key={l.idlista}
                            sx={{ border: "1px solid rgba(0,0,0,0.06)" }}
                        >
                            <CardContent
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                }}
                            >
                                <Typography sx={{ fontWeight: 900 }} noWrap>
                                    {l.nombre}
                                </Typography>

                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() =>
                                        navigate(`/listas/${l.idlista}`)
                                    }
                                >
                                    Abrir
                                </Button>
                            </CardContent>
                        </Card>
                    ))}
                </Stack>
            )}

            {/* Modal crear lista */}
            <CrearListaEnSalaModal
                open={openCrearLista}
                onClose={() => setOpenCrearLista(false)}
                salaId={idSala}
                onCreated={cargarSala}
            />

            {/* Modal invitar */}
            {openInvitar && (
                <InvitarUsuarioModal
                    salaId={idSala}
                    onClose={() => setOpenInvitar(false)}
                />
            )}

            {/* Modal editar sala */}
            <Dialog open={openEditar} onClose={() => setOpenEditar(false)}>
                <DialogTitle>Editar sala</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Nombre"
                        value={nombre}
                        onChange={(e) => setNombre(e.target.value)}
                        fullWidth
                        sx={{ mt: 1 }}
                    />
                    <TextField
                        label="Descripción"
                        value={descripcion}
                        onChange={(e) => setDescripcion(e.target.value)}
                        fullWidth
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenEditar(false)}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleGuardarSala}
                        disabled={guardando}
                    >
                        Guardar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal eliminar sala */}
            <Dialog open={openEliminar} onClose={() => setOpenEliminar(false)}>
                <DialogTitle>Eliminar sala</DialogTitle>
                <DialogContent>
                    <Typography>
                        ¿Seguro que quieres eliminar esta sala?
                        Esta acción no se puede deshacer.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenEliminar(false)}>
                        Cancelar
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={handleEliminarSala}
                    >
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
